'use client'

import { useT, useFormat } from '@/lib/i18n'

/**
 * Drawer for one v3 anomaly case.
 *
 * Reading order is deliberate and flat, and it now starts from the case itself
 * rather than from a score: the raw facts → the physical hand-over → why the two
 * shipments are treated as one movement → how unusual it is → what exactly is
 * unusual, with numbers → what to check → comparable cases → provenance.
 *
 * The order was inverted on purpose (stage 41). An analyst opening a case first
 * asks «what is this shipment», not «what did the model score it»: the numbers
 * mean something only once the wagon, the cargo and the two stations are known.
 * Section order is the same for every scenario; a section with nothing to show
 * is omitted and the remaining ones keep their relative order.
 *
 * Two rules shape everything below:
 *  • Numbers in «Почему этот кейс необычен» come from the service's verified
 *    evidence (anomaly_evidence_v1). A reason with no evidence is not narrated
 *    with a generic template — it is left out, because a template sentence reads
 *    as a finding.
 *  • Model names, cohort ids, review queues and reason codes live ONLY in the
 *    collapsed technical block.
 */

import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
// Section / Fact / Metric moved out of this file unchanged, so the scenarios 6–7 drawer
// renders the same cards instead of growing a second look-alike set.
import { EtsngClassificationRows, Fact, Metric, Section } from './case-drawer-primitives'
import { resolveCaseClassification } from '@/lib/etsng-classification'
import { AnomalyEvidenceTable } from './anomaly-evidence-table'
import { freelanceMappingView, matchingCasesLabel } from '../freelance-case-mapping'
// Та же панель примеров, что в drawer'е сценариев 6–7: второго компонента нет,
// различается только источник строк (кейс+сценарий вместо кандидатной пары).
import { EvidenceExamplesPanel } from '../cross-unit/components/evidence-examples'
import { useAnomalyCaseDetailV3, useSimilarCasesV3 } from '../anomalies-v3.hooks'
import {
  anomalyLevelLabels,
  anomalyCohortLabels,
  type AnomalyCaseRow,
  type AnomalyExplanation,
  type AnomalyLevel,
  type SimilarCaseRow,
  type FreelanceCaseMapping,
} from '../anomaly-cases-v3.types'
import {
  buildTransportLegs,
  buildChecklist,
  buildLinkage,
  CARGO_MATCH_SOURCE_FIELD,
  type TriState,
  type LinkageCriterion,
} from '../anomaly-case-analysis'
import { formatWeightDifference } from '../weight-format'
import {
  EMPTY_STATUS_UNAVAILABLE_TOOLTIP,
  NUMERIC_ZERO_TOOLTIP,
  WAGON_AS_CARGO_NO_WEIGHT_TEXT,
  WAGON_AS_CARGO_TEXT,
  anomalyWeightSemantics,
  isWagonAsCargo,
} from '../anomaly-semantics'
import {
  ANOMALY_DISCLAIMER,
  NO_EVIDENCE_TEXT,
  noEvidenceCause,
  buildCaseExplanation,
  buildEvidenceChecklist,
  buildTopSummary,
  explanationContext,
  scoreOutOfTen,
} from '../anomaly-explanation'

const LEVEL_TEXT: Record<AnomalyLevel, string> = {
  priority_review: 'text-risk-high',
  high: 'text-risk-high',
  elevated: 'text-risk-medium',
  baseline: 'text-risk-low',
}

type DetailRow = AnomalyCaseRow & {
  anomalyExplanation?: AnomalyExplanation
  /** Доказанное соответствие кейсу реестра Freelancity; отсутствует, если его нет. */
  freelanceCase?: FreelanceCaseMapping
}

export function AnomalyCaseDrawer({
  caseId,
  scenarioId,
  onClose,
}: {
  caseId: string | null
  scenarioId: number | null
  onClose: () => void
}) {
  const t = useT()
  const f = useFormat()
  const { data, isLoading } = useAnomalyCaseDetailV3(caseId, scenarioId)
  const row = data?.row as DetailRow | undefined

  useEffect(() => {
    if (!caseId) return
    const h = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', h)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', h)
      document.body.style.overflow = ''
    }
  }, [caseId, onClose])

  if (!caseId) return null

  // Identity used in the prose (wagon/container number, cargo codes) comes from
  // the row's own authoritative fields — the same ones «Детали кейса» shows.
  const explanation = row ? buildCaseExplanation(f, row.anomalyExplanation, explanationContext(row)) : null

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div
        // 620 → 940px. The historical examples are a 9-column table now, and at 620px
        // every date, shipment number and cargo code was cut mid-value. 940px is the
        // narrowest width at which all of them render in full without an inner scroll;
        // it is still a drawer — the case list stays visible behind it — and on a
        // narrower screen `w-full` shrinks it and the table scrolls inside its own box.
        className="flex h-full w-full max-w-[940px] flex-col overflow-y-auto border-l border-[var(--border-default)] bg-[var(--bg-canvas)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 1 — header (model name intentionally absent; it lives in tech details) */}
        <header className="flex shrink-0 items-center justify-between border-b border-[var(--border-default)] bg-[var(--bg-surface)] px-5 py-3">
          <div className="min-w-0">
            <h2 className="text-[14px] font-semibold text-[var(--text-primary)]">
              Кейс #{caseId} · Сценарий {scenarioId}
            </h2>
            {/* Номер(а) ТОГО ЖЕ физического кейса во вкладке «Кейсы» — подзаголовок
                под именем кейса, а не строка в перечне фактов: аналитик сверяет
                его глазами с реестром до того, как начнёт читать сам кейс.
                Одно доказанное соответствие — один номер, как и раньше. Несколько
                доказанных — показываются ВСЕ и их количество: реестр содержит
                несколько записей на одну перевозку, и выбирать за пользователя
                нечего. При NOT_FOUND и любом отказе слоя строки нет вовсе — ни
                прочерка, ни «не найден». Показываются те самые числа, которые
                пользователь найдёт в таблице «Кейсы», а не внутренние
                идентификаторы API и не номера локального реестра. */}
            <FreelanceCaseLine mapping={row?.freelanceCase} />
          </div>
          <button type="button" onClick={onClose} className="rounded-[3px] px-2 py-1 text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]">✕</button>
        </header>

        {isLoading || !row || !explanation ? (
          <div className="p-6 text-[13px] text-[var(--text-tertiary)]">{isLoading ? t('common.state.loading') : t('analytics.caseDrawer.notFound')}</div>
        ) : (
          <div className="space-y-5 p-5">
            {/* 2 — raw case facts: the analyst reads what this case IS before any score */}
            <Section title={t('analytics.caseDrawer.title')}>
              <dl className="grid grid-cols-1 gap-1 text-[12px]">
                <Fact k={t('analytics.caseDrawer.caseScenario')} v={`#${row.caseId} · Сценарий ${row.scenarioId}`} />
                {/* Груз пары — две ноги, а не одно значение. `cargo` — это
                    `import_cargo_name`, `cargoExport` — `export_cargo_name`
                    (build_anomaly_cases_v3.py:215–216), и общая подпись «Груз»
                    выдавала импортную сторону за характеристику всей перевозки.
                    Коды в строке кейса относятся ТОЛЬКО к импортной ноге
                    (`cargoEtsng` = import_etsng, `cargoGng` = import_gng), поэтому
                    стоят под своим грузом и под экспортный не дублируются:
                    экспортных кодов в этом ответе нет, а равенство доказано лишь
                    как общий признак смены кода — он показан в разделе связи. */}
                <Fact k={t('analytics.caseDrawer.cargoImport')} v={row.cargo} extra={row.cargoEtsng ? `${t('analytics.caseDrawer.etsngPrefix')} ${row.cargoEtsng}${row.cargoGng ? `, ${t('analytics.caseDrawer.gngPrefix')} ${row.cargoGng}` : ''}` : undefined} />
                <Fact k={t('analytics.caseDrawer.cargoExport')} v={row.cargoExport} />
                {/* Классификация ЕТСНГ — справочные уровни над кодом груза (замороженный
                    снимок НСИ, `@/lib/etsng-classification`). Экспортного кода этот контур
                    не отдаёт, зато отдаёт `cargoCodeChanged`: пока источник сам утверждает,
                    что код не менялся, блок один на обе ноги. Ни балл, ни уровень, ни
                    причины классификация не читает и не меняет. */}
                <CargoClassification row={row} />
                <Fact k={t('common.field.route')} v={row.route} />
                <Fact k={t('analytics.caseDrawer.datesImpExp')} v={row.importDate && row.exportDate ? `${row.importDate} → ${row.exportDate}` : null} extra={row.daysBetween != null ? `${row.daysBetween} дн` : undefined} />
                <Fact k={t('analytics.caseDrawer.wagonContainer')} v={row.wagon || row.container} />
                <Fact k={t('analytics.caseDrawer.stationsKz')} v={row.importStationTo && row.exportStationFrom ? `${row.importStationTo} → ${row.exportStationFrom}` : null} />
                <WeightFact row={row} />
                <Fact k={t('analytics.caseDrawer.shipmentsImpExp')} v={row.importShipmentId && row.exportShipmentId ? `${row.importShipmentId} / ${row.exportShipmentId}` : null} />
                <Fact k={t('analytics.caseDrawer.payerCodeImpExp')} v={row.importPayerCode || row.exportPayerCode ? `${row.importPayerCode ?? '—'} / ${row.exportPayerCode ?? '—'}` : null} />
                <Fact k={t('analytics.caseDrawer.binImpExp')} v={row.importReceiverBin || row.exportSenderBin ? `${row.importReceiverBin ?? '—'} / ${row.exportSenderBin ?? '—'}` : null} />
                <Fact k={t('analytics.caseDrawer.countriesImpExp')} v={row.importCountryFrom && row.exportCountryTo ? `${row.importCountryFrom} → ${row.exportCountryTo}` : null} />
                <Fact k={t('analytics.caseDrawer.comparisonGroup')} v={anomalyCohortLabels(t)[row.anomalyCohort] ?? row.anomalyCohort} />
              </dl>
              {/* Пояснения о массе и о вагоне на своих осях стоят рядом с фактами, а не
                  в объяснении причин: они относятся к самим данным кейса.
                  В сценарии 5 их не показываем — там «Детали кейса» остаются голым
                  перечнем полей. Компонент возвращает null, поэтому пустого контейнера,
                  отступа или разделителя после него не остаётся. */}
              <EmptinessNotes row={row} />
            </Section>

            {/* 3 — physical hand-over: how the two shipments meet on the ground */}
            <TransportLegsSection row={row} />

            {/* 4 — merged linkage: required scenario criteria first, context after */}
            <LinkageSection row={row} />

            {/* 5 — plain-language summary + the two scores, no model vocabulary */}
            <Section title={t('analytics.caseDrawer.whyAttention')}>
              <p className="text-[12px] leading-relaxed text-[var(--text-secondary)]">
                {buildTopSummary(row, explanation)}
              </p>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <Metric
                  label={t('analytics.caseDrawer.howUnusual')}
                  value={scoreOutOfTen(row.anomalyScore)}
                  accent={LEVEL_TEXT[row.anomalyLevel]}
                  hint={anomalyLevelLabels(t)[row.anomalyLevel]}
                />
                <Metric
                  label={t('analytics.caseDrawer.priorityByKnown')}
                  value={scoreOutOfTen(row.mlScore)}
                  hint={t('analytics.caseDrawer.separateScore')}
                />
              </div>
            </Section>

            {/* 6 — the acceptance requirement: quantified, case-specific reasons.
                Conclusion first, then one card per reason: title = the finding,
                one short sentence = the evidence, meta lines = the details a
                reader only needs on demand. */}
            <Section title={t('analytics.caseDrawer.whyUnusual')}>
              {explanation.isEmpty ? (
                /* Причина называется конкретно: «данные не сохранены» верно лишь
                   для 234 кейсов из 7617 без оснований, а у остальных числового
                   ОСНОВАНИЯ просто не существует — предыдущих перевозок этой
                   единицы в доступной истории нет, сравнивать не с чем. */
                <p className="rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-3 py-2.5 text-[12px] leading-relaxed text-[var(--text-secondary)]">
                  {explanation.emptyText ?? NO_EVIDENCE_TEXT}
                </p>
              ) : (
                <>
                  {explanation.summary && (
                    <p className="mb-3 text-[12px] leading-relaxed text-[var(--text-primary)]">
                      {explanation.summary}
                    </p>
                  )}
                  <ul className="space-y-2">
                    {explanation.reasons.map((r) => (
                      <li key={r.code} className="rounded-[3px] border-l-2 border-l-[var(--accent-data)] bg-[var(--bg-subtle)] px-3 py-2">
                        <div className="text-[12px] font-medium text-[var(--text-primary)]">{r.title}</div>
                        <p className="mt-1 text-[12px] leading-relaxed text-[var(--text-secondary)]">{r.text}</p>
                        {/* Stage 39: the numbers moved out of the sentence into a table —
                            same component the scenarios 6–7 drawer uses. */}
                        <AnomalyEvidenceTable
                          rows={r.evidence}
                          note={r.note}
                          renderExamples={(d) => (
                            <EvidenceExamplesPanel
                              source={{ kind: 'case', caseId: row.caseId, scenarioId: row.scenarioId }}
                              drilldown={d}
                            />
                          )}
                        />
                      </li>
                    ))}
                  </ul>
                </>
              )}
              {explanation.hasWithheld && !explanation.isEmpty && (
                <p className="mt-2 text-[10px] leading-relaxed text-[var(--text-tertiary)]">
                  {t('analytics.drawer.featuresPartial')}
                </p>
              )}
            </Section>

            {/* 7 — checklist, built only from supported evidence + linkage facts */}
            <Section title={t('analytics.caseDrawer.checklist')}>
              <ul className="space-y-1.5">
                {/* Stage 39: the evidence-backed lines come first and carry the same
                    numbers as the tables above; the factual linkage items follow. */}
                {[...buildEvidenceChecklist(explanation.reasons),
                  ...buildChecklist(row, t, explanation.reasons.map((r) => r.code))].map((line, i) => (
                  <li key={i} className="flex gap-2 text-[11px] text-[var(--text-secondary)]">
                    {/* параметр стрелки переименован в line: `t` здесь — переводчик
                        из useT(), и прежнее имя его затеняло. */}
                    <span className="text-[var(--text-tertiary)]">☐</span>{line}
                  </li>
                ))}
              </ul>
            </Section>

            {/* 8 — comparable cases */}
            <SimilarCasesSection caseId={row.caseId} scenarioId={row.scenarioId} />

            {/* Single disclaimer for the whole drawer. */}
            <p className="text-[10px] leading-relaxed text-[var(--text-tertiary)]">{ANOMALY_DISCLAIMER}</p>

            {/* 9 — provenance, collapsed */}
            <TechDetails row={row} />
          </div>
        )}
      </div>
    </div>
  )
}

// ── sections ─────────────────────────────────────────────────────────────────

/**
 * «Почему импорт и экспорт связаны» — the old pair-indicator block and scenario
 * rule check, merged. Required criteria carry the scenario's own thresholds;
 * optional facts are informational and never rendered as a failed requirement.
 */
/**
 * Подзаголовок «Кейс(ы) во вкладке «Кейсы»» под именем кейса аномалии.
 *
 * Компактный и подчинённый заголовку: ни отдельной карточки, ни секции, ни модального
 * окна. Разметка решений не принимает — вся логика состояний в `freelanceMappingView`.
 *
 * Номера остаются ОБЫЧНЫМ ТЕКСТОМ: безопасный маршрут во вкладку «Кейсы» для этих
 * номеров пока не сертифицирован, а придумывать навигацию нельзя.
 */
function FreelanceCaseLine({ mapping }: { mapping?: FreelanceCaseMapping }) {
  const [expanded, setExpanded] = useState(false)
  const view = freelanceMappingView(mapping)
  if (view.kind === 'hidden') return null

  if (view.kind === 'single') {
    return (
      <p className="mt-0.5 truncate text-[11px] text-[var(--text-tertiary)]" data-freelance-case={view.caseNumber}>
        Кейс во вкладке «Кейсы»{' '}
        <span className="text-[var(--text-secondary)]">#{view.caseNumber}</span>
      </p>
    )
  }

  // Несколько доказанных совпадений. Сразу показываются первые пять; остальные — по
  // клику, чтобы группа из десятков номеров не растянула шапку.
  const shown = expanded ? view.caseNumbers : view.inline
  return (
    <div
      className="mt-0.5 text-[11px] leading-relaxed text-[var(--text-tertiary)]"
      data-freelance-cases={view.caseNumbers.join(',')}
      data-freelance-case-count={view.total}
    >
      <span>Кейсы во вкладке «Кейсы»</span>
      <div className="flex flex-wrap items-center gap-x-1.5 text-[var(--text-secondary)]">
        {shown.map((n, i) => (
          <span key={n}>
            #{n}
            {i < shown.length - 1 && <span className="ml-1.5 text-[var(--text-tertiary)]">·</span>}
          </span>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-x-2">
        <span>{matchingCasesLabel(view.total)}</span>
        {view.rest.length > 0 && (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            className="rounded-[3px] text-[10px] text-[var(--accent-data)] underline decoration-dotted hover:text-[var(--text-primary)]"
          >
            {expanded ? '▾ Свернуть' : '▸ Показать все'}
          </button>
        )}
      </div>
    </div>
  )
}

function LinkageSection({ row }: { row: AnomalyCaseRow }) {
  const t = useT()
  const link = buildLinkage(row, t)
  return (
    <Section title={t('analytics.linkage.title')}>
      <p className="mb-2 text-[11px] text-[var(--text-tertiary)]">
        Обязательные признаки сценария {link.scenarioId}: {link.requiredCount}. Совпало {link.matchedCount}
        {link.unmatchedCount > 0 && `, не совпало ${link.unmatchedCount}`}
        {link.unknownCount > 0 && `, недостаточно данных ${link.unknownCount}`}.
      </p>
      <div className="space-y-1">
        {link.required.map((c) => <CriterionRow key={c.key} c={c} required />)}
      </div>
      {link.optional.length > 0 && (
        <div className="mt-3 border-t border-[var(--border-subtle)] pt-2">
          <div className="mb-1 text-[10px] uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{t('analytics.linkage.extra')}</div>
          <div className="space-y-1">
            {link.optional.map((c) => <CriterionRow key={c.key} c={c} />)}
          </div>
        </div>
      )}
    </Section>
  )
}

function CriterionRow({ c, required }: { c: LinkageCriterion; required?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-2 text-[12px]">
      <div className="min-w-0">
        <span className={required ? 'text-[var(--text-secondary)]' : 'text-[var(--text-tertiary)]'}>{c.label}</span>
        {c.detail && <span className="ml-1 text-[10px] text-[var(--text-tertiary)]">— {c.detail}</span>}
      </div>
      {/* Optional facts get a neutral dot: they are context, not a check that failed. */}
      {required ? <TriMark state={c.state} /> : <span className="text-[var(--text-tertiary)]" aria-hidden="true">·</span>}
    </div>
  )
}

function TransportLegsSection({ row }: { row: AnomalyCaseRow }) {
  const t = useT()
  const legs = buildTransportLegs(row)
  return (
    <Section title={t('analytics.legs.title')}>
      <div className="space-y-2 text-[12px]">
        <LegBlock title={t('analytics.legs.importLeg')} rows={[
          [t('common.field.countryFrom'), legs.importLeg.country],
          [t('analytics.legs.shipmentNo'), legs.importLeg.shipment],
          [t('common.field.date'), legs.importLeg.date],
          [t('analytics.legs.arrivalStationKz'), legs.importLeg.kzArrival],
          [t('analytics.legs.borderIn'), legs.importLeg.border],
        ]} />
        <div className="rounded-[3px] bg-[var(--bg-subtle)] px-2.5 py-2">
          <div className="text-[10px] uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{t('analytics.legs.innerJunction')}</div>
          <Fact k={t('analytics.legs.destStationImport')} v={legs.internal.importDest} />
          <Fact k={t('analytics.legs.depStationExport')} v={legs.internal.exportOrigin} />
          <Fact k={t('analytics.legs.stationsMatch')} v={triLabel(legs.internal.match)} />
          <Fact k={t('analytics.legs.interval')} v={legs.internal.days != null ? `${legs.internal.days} ${t('analytics.legs.daysShort')}` : '—'} />
        </div>
        <LegBlock title={t('analytics.legs.exportLeg')} rows={[
          [t('common.field.countryTo'), legs.exportLeg.country],
          [t('analytics.legs.shipmentNo'), legs.exportLeg.shipment],
          [t('common.field.date'), legs.exportLeg.date],
          [t('analytics.legs.depStationKz'), legs.exportLeg.kzDeparture],
          [t('analytics.legs.borderOut'), legs.exportLeg.border],
        ]} />
      </div>
    </Section>
  )
}

// ── sub-components ────────────────────────────────────────────────────────────

function TriMark({ state }: { state: TriState }) {
  const t = useT()
  if (state === 'match') return <span className="text-risk-low" title={t('analytics.tri.match')}>✓</span>
  if (state === 'mismatch') return <span className="text-risk-high" title={t('analytics.tri.noMatch')}>✗</span>
  return <span className="text-[var(--text-tertiary)]" title={t('analytics.tri.notEnough')}>—</span>
}

function triLabel(s: TriState): string {
  return s === 'match' ? 'Да' : s === 'mismatch' ? 'Нет' : '—'
}

function LegBlock({ title, rows }: { title: string; rows: [string, string | null][] }) {
  return (
    <div className="rounded-[3px] bg-[var(--bg-subtle)] px-2.5 py-2">
      <div className="text-[10px] uppercase tracking-[0.04em] text-[var(--text-tertiary)]">{title}</div>
      {rows.map(([k, v]) => <Fact key={k} k={k} v={v} />)}
    </div>
  )
}

function SimilarCasesSection({ caseId, scenarioId }: { caseId: string; scenarioId: number }) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const [page, setPage] = useState(1)
  const { data, isLoading } = useSimilarCasesV3(caseId, scenarioId, page, open)
  const total = data?.total ?? 0
  const pageSize = data?.pageSize ?? 5
  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  return (
    <section className="rounded-[4px] border border-[var(--border-subtle)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[12px] font-semibold uppercase tracking-[0.06em] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]"
        aria-expanded={open}
      >
        {open ? '▾' : '▸'} {t('analytics.similar.title')}{open && data ? ` (${total})` : ''}
      </button>
      {open && (
        <div className="space-y-2 border-t border-[var(--border-subtle)] p-3">
          {isLoading ? (
            <p className="text-[12px] text-[var(--text-tertiary)]">{t('common.state.loading')}</p>
          ) : total === 0 ? (
            <p className="text-[12px] text-[var(--text-tertiary)]">{t('analytics.drawer.similarSemantic')}</p>
          ) : (
            <>
              <div className="space-y-2">
                {data!.rows.map((s) => <SimilarCard key={`${s.caseId}_${s.scenarioId}`} s={s} />)}
              </div>
              <div className="flex items-center justify-between text-[11px] text-[var(--text-secondary)]">
                <span>всего {total}</span>
                <span className="flex items-center gap-2">
                  <button type="button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="rounded-[3px] border border-[var(--border-default)] px-2 py-0.5 disabled:opacity-40">←</button>
                  стр. {data!.page} / {totalPages}
                  <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="rounded-[3px] border border-[var(--border-default)] px-2 py-0.5 disabled:opacity-40">→</button>
                </span>
              </div>
            </>
          )}
          <p className="text-[10px] leading-relaxed text-[var(--text-tertiary)]">{data?.note ?? t('analytics.similar.note')}</p>
        </div>
      )}
    </section>
  )
}

function SimilarCard({ s }: { s: SimilarCaseRow }) {
  const t = useT()
  const simWeight = formatWeightDifference(s, t)
  return (
    <div className="rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-surface)] p-2.5">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[12px] text-[var(--text-primary)]">#{s.caseId} · Сц.{s.scenarioId}</span>
        <span className="flex items-center gap-2 text-[11px]">
          <span className={cn('font-mono tabular-nums', LEVEL_TEXT[s.anomalyLevel])}>A {fmt2(s.anomalyScore)}</span>
          <span className={cn('font-mono tabular-nums', mlText(s.mlLevel))}>ML {fmt2(s.mlScore)}</span>
          <span
            className="rounded-[2px] bg-[var(--bg-subtle)] px-1 py-px text-[10px] text-[var(--text-secondary)]"
            title="{t('analytics.drawer.matchPointsNote')}"
          >
            {t('analytics.similar.pointsLabel')} {fmtPoints(s.similarity.similarityScore)}
          </span>
        </span>
      </div>
      <div className="mt-1 text-[11px] text-[var(--text-secondary)]">
        {s.cargo ?? '—'} · {s.route ?? '—'} · {s.wagon || s.container || '—'} · {s.daysBetween != null ? `${s.daysBetween} ${t('analytics.legs.daysShort')}` : '—'} · <span title={simWeight.tooltip}>Δ{simWeight.displayValue}</span>
      </div>
      <div className="mt-0.5 text-[10px] text-[var(--text-tertiary)]">
        {s.importDate ?? '—'} → {s.exportDate ?? '—'} · {t('analytics.similar.shipmentShort')} {s.importShipmentId ?? '—'}/{s.exportShipmentId ?? '—'} · {t('analytics.similar.payerShort')} {s.importPayerCode ?? '—'}/{s.exportPayerCode ?? '—'}
      </div>
      {s.matches.length > 0 && (
        <div className="mt-1 text-[10px] text-[var(--text-secondary)]">
          <span className="font-medium text-[var(--text-tertiary)]">{t('analytics.similar.matched')}</span> {s.matches.join('; ')}
        </div>
      )}
      {s.mainDifferences.length > 0 && (
        <div className="mt-0.5 text-[10px] text-[var(--text-tertiary)]">
          <span className="font-medium">{t('analytics.similar.differs')}</span> {s.mainDifferences.join('; ')}
        </div>
      )}
    </div>
  )
}

/** Comparison points with Russian decimal comma — «9,0», «8,5». NOT a model score. */
function fmtPoints(n: number): string {
  return n.toFixed(1).replace('.', ',')
}

function fmt2(n: number | null): string {
  return n === null || n === undefined ? '—' : n.toFixed(2)
}

function TechDetails({ row }: { row: DetailRow }) {
  const [open, setOpen] = useState(false)
  const exp = row.anomalyExplanation
  return (
    <section className="rounded-[4px] border border-[var(--border-subtle)]">
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[11px] font-medium uppercase tracking-[0.06em] text-[var(--text-secondary)] hover:bg-[var(--bg-subtle)]">
        {open ? '▾' : '▸'} Технические сведения
      </button>
      {open && (
        <dl className="space-y-1 border-t border-[var(--border-subtle)] px-3 py-2 font-mono text-[10px] text-[var(--text-tertiary)]">
          <Line k="pair_id" v={row.pairId} />
          <Line k="mapping" v={`${row.mappingStatus} · ${row.mappingRuleId}`} />
          <Line k="candidate_family" v={`${row.candidateFamily} / ${row.physicalUnitType}`} />
          <Line k="serve_source" v={row.serveSource} />
          <Line k="anomaly_model" v={`${row.anomalyModelVersion} / ${row.anomalyBundleVersion}`} />
          <Line k="anomaly_score / percentile" v={`${fmt(row.anomalyScore)} / ${row.anomalyPercentile != null ? `${row.anomalyPercentile.toFixed(2)}%` : '—'}`} />
          <Line k="anomaly_cohort_id" v={row.anomalyCohort} />
          <Line k="ml_track / cohort" v={`${row.mlTrack} / ${row.mlCohort}`} />
          <Line k="ml_score" v={fmt(row.mlScore)} />
          <Line k="review" v={`${row.reviewTier} / ${row.reviewQueue}`} />
          <Line k="anomaly_reasons" v={row.anomalyReasons.join('; ') || '—'} />
          <Line k="evidence_version" v={exp?.version ?? '—'} />
          <Line k="evidence_status" v={exp?.evidenceStatus ?? 'unavailable'} />
          {!exp?.reasons?.length && (
            <Line k="evidence_absence_cause" v={noEvidenceCause(exp, row.anomalyCohort)} />
          )}
          <Line k="evidence_source" v={exp?.reasons?.[0]?.evidenceSource ?? '—'} />
          <Line k="reasons_without_evidence" v={exp?.unsupportedReasonCodes?.join('; ') || '—'} />
          <Line k="cargo_match_source" v={CARGO_MATCH_SOURCE_FIELD} />
        </dl>
      )}
    </section>
  )
}

function fmt(n: number | null): string {
  return n === null || n === undefined ? '—' : n.toFixed(4)
}
function mlText(level: string): string {
  return level === 'high' ? 'text-risk-high' : level === 'medium' ? 'text-risk-medium' : 'text-risk-low'
}

/**
 * «Вес имп → эксп» rendered through the shared formatter, so the drawer can
 * never say «вес совпадает» for a difference the table showed as «< 0.1%».
 * A missing weight still shows whichever side is known.
 */
/**
 * Классификация ЕТСНГ груза кейса — две справочные строки под грузом.
 *
 * ОТКУДА КОД. `row.cargoEtsng` — это `import_etsng` витрины (build_anomaly_cases_v3.py),
 * то есть код ИМПОРТНОЙ ноги; экспортного кода этот ответ не несёт вовсе. Поэтому один
 * общий блок рисуется только когда витрина сама подтвердила, что код не менялся
 * (`cargoCodeChanged === false`) — иначе импортная сторона выдавалась бы за обе.
 *
 * ВЕДУЩИЙ НОЛЬ. Код здесь лежит без него («11005» вместо «011005») у 9 986 из 25 671
 * кейсов — нормализация в `resolveCaseClassification` обязательна, иначе классификация
 * этих кейсов просто не нашлась бы.
 *
 * НИЧЕГО НЕ ЛОМАЕТ. Кода нет, код не цифровой, справочник его не знает — компонент
 * возвращает `null`, и в карточке просто нет этих двух строк. Ни «—», ни «undefined».
 */
function CargoClassification({ row }: { row: AnomalyCaseRow }) {
  const view = resolveCaseClassification(row.cargoEtsng, null, {
    sameCodeAsserted: row.cargoCodeChanged === false,
  })
  if (view.kind !== 'shared') return null
  return <EtsngClassificationRows classification={view.classification} heading="Классификация ЕТСНГ" />
}

function WeightFact({ row }: { row: AnomalyCaseRow }) {
  const t = useT()
  // Anomaly-specific presenter поверх того же статуса: он различает «нет данных»,
  // числовой ноль и посчитанную разницу, тогда как общий форматтер сводит первые два
  // к «—». Ни одно вычисление при этом не меняется.
  const w = anomalyWeightSemantics(row)
  return (
    <Fact
      k={t('analytics.weight.title')}
      v={w.detailText}
      title={w.tooltip}
      dataAttr={{ 'data-weight-kind': w.kind }}
    />
  )
}

/**
 * Короткие пояснения, которые снимают ложное впечатление о порожности.
 *
 * Появляются только там, где им есть на что опереться: наименование груза с формулировкой
 * «перевозимые на своих осях» и/или отсутствующая масса. Статус «Порожний» не выводится
 * никогда — подтверждающего поля в данных кейса нет.
 *
 * СЦЕНАРИЙ 5 ИСКЛЮЧЁН. В его карточке «Детали кейса» остаются голым перечнем полей: там
 * же снята и раскрываемая справка о самом сценарии. Сценарии 1–4 работают как прежде —
 * их карточки эта правка не трогает.
 */
function EmptinessNotes({ row }: { row: AnomalyCaseRow }) {
  const t = useT()
  if (row.scenarioId === 5) return null
  const w = anomalyWeightSemantics(row)
  const wagonAsCargo = isWagonAsCargo(row)
  const notes: string[] = []
  if (wagonAsCargo) notes.push(WAGON_AS_CARGO_TEXT)
  if (w.kind === 'missing_both' || w.kind === 'missing_import' || w.kind === 'missing_export') {
    notes.push(wagonAsCargo
      ? WAGON_AS_CARGO_NO_WEIGHT_TEXT
      : t('analytics.drawer.weightMissing'))
  } else if (w.kind === 'numeric_zero') {
    notes.push(NUMERIC_ZERO_TOOLTIP)
  }
  if (notes.length === 0) return null
  return (
    <p
      data-block="emptiness-note"
      title={EMPTY_STATUS_UNAVAILABLE_TOOLTIP}
      className="mt-2 rounded-[3px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] px-2.5 py-1.5 text-[11px] leading-relaxed text-[var(--text-secondary)]"
    >
      {notes.join(' ')}
    </p>
  )
}

function Line({ k, v }: { k: string; v: string }) {
  return <div className="flex gap-2"><dt className="shrink-0 text-[var(--text-tertiary)]">{k}:</dt><dd className="break-all text-[var(--text-secondary)]">{v}</dd></div>
}
